import { FC } from "react";
import { TrendingUp, TrendingDown, Equal } from "lucide-react";
import { useTranslation } from "react-i18next";
import { formatCurrency } from "@/utils/currency";

interface Props {
  previousTotal: number;
  newTotal: number;
  difference: number;
}

type Trend = "up" | "down" | "flat";

const getTrend = (difference: number): Trend =>
  difference > 0 ? "up" : difference < 0 ? "down" : "flat";

const TREND_COLOR: Record<Trend, string> = {
  up: "text-rose-600",
  down: "text-emerald-600",
  flat: "text-muted-foreground",
};

const TREND_ICON: Record<Trend, typeof TrendingUp> = {
  up: TrendingUp,
  down: TrendingDown,
  flat: Equal,
};

const SummaryDiff: FC<Props> = ({ previousTotal, newTotal, difference }) => {
  const { t, i18n } = useTranslation(["contract-updates"]);

  const trend = getTrend(difference);
  const color = TREND_COLOR[trend];
  const TrendIcon = TREND_ICON[trend];

  const diffLabel =
    trend === "up"
      ? t("contract-updates:diff-increase", "(Increase)")
      : trend === "down"
        ? t("contract-updates:diff-decrease", "(Decrease)")
        : t("contract-updates:diff-no-change", "(No change)");

  return (
    <div className="w-full">
      <h3 className="text-lg font-semibold mb-3">
        {t("contract-updates:summary-title", "Summary of changes:")}
      </h3>
      <div className="flex flex-col">
        <div className="flex items-center justify-between border-b border-gray-100 py-1.5">
          <p className="text-base">
            {t("contract-updates:previous-total", "Previous total")}
          </p>
          <p className="text-base font-semibold">
            {formatCurrency(previousTotal, i18n.language)}
          </p>
        </div>
        <div className="flex items-center justify-between border-b border-gray-100 py-1.5">
          <p className="text-base">
            {t("contract-updates:new-total", "New total")}
          </p>
          <p className="text-base font-semibold">
            {formatCurrency(newTotal, i18n.language)}
          </p>
        </div>
        <div className="flex items-center justify-between py-1.5">
          <div className="flex items-center gap-1">
            <p className="text-base">
              {t("contract-updates:total-difference", "Total difference")}
            </p>
            <p className={`text-sm ${color}`}>{diffLabel}</p>
          </div>
          <div className="flex items-center gap-2">
            <TrendIcon className={`size-4 ${color}`} />
            <p className={`text-base font-semibold ${color}`}>
              {formatCurrency(Math.abs(difference), i18n.language)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SummaryDiff;
